import { DateRange } from '@mui/lab';
import { InvoiceSettings } from './client-directory';
import { User } from './user';
import { InvoicingState, Order } from './order';
import { ConnectedLab, Organization, PaymentDetails } from './organization';
import { Communication, OrganizationContact } from './contact';

export type TotalAmount = {
    totalPrice: number;
    vatAmount?: number;
    currency?: string;
    averagePrice?: number;
    totalIncVat?: number;
};

export type OrderSummary = {
    totalOrderCount: number;
    totalAmount: TotalAmount;
};

export enum OrderStatus {
    ALL = 'ALL',
    SENT_BY_CUSTOMER = 'SENT_BY_CUSTOMER',
    ACCEPTED_BY_LAB = 'ACCEPTED_BY_LAB',
    SENT_BY_LAB = 'SENT_BY_LAB',
    ORDER_APPROVED = 'ORDER_APPROVED',
    CANCELLED = 'CANCELLED',
    DECLINED = 'DECLINED',
}

export enum InvoiceStatus {
    ALL = 'ALL',
    CREATED = 'CREATED',
    SCHEDULED = 'SCHEDULED',
    PROCESSING = 'PROCESSING',
    SENT = 'SENT',
    ERROR = 'ERROR',
    // Exists in Fortnox, delivery-note attachment failed; needs a human, not retryable (DD-3504).
    EXTERNAL_INVOICE_ERROR = 'EXTERNAL_INVOICE_ERROR',
    VOIDED = 'VOIDED',
}

export enum ReadyForInvoiceOptions {
    ALL = 'ALL',
    READY_FOR_INVOICE = 'READY_FOR_INVOICE',
    NOT_READY = 'NOT_READY',
    INVOICED = 'INVOICED',
    SHOULD_NOT_INVOICE = 'SHOULD_NOT_INVOICE',
}

export enum InvoiceType {
    ALL = 'ALL',
    SINGLE = 'SINGLE',
    COLLECTIVE_INVOICE = 'COLLECTIVE',
    COLLECTIVE_DENTIST = 'COLLECTIVE_DENTIST',
}

// Backed values
export enum InvoicingType {
    SINGLE = 'SINGLE',
    COLLECTIVE = 'COLLECTIVE',
    COLLECTIVE_DENTIST = 'COLLECTIVE_DENTIST',
}

// Backend values
export enum InvoiceDelivery {
    SCHEDULED_ELECTRONIC = 'SCHEDULED_ELECTRONIC',
    ELECTRONIC_BILLING = 'ELECTRONIC_BILLING',
    PRINT = 'PRINT',
    EMAIL = 'EMAIL',
    EXTERNAL_INVOICE_SYSTEM = 'EXTERNAL_INVOICE_SYSTEM',
}

export enum InvoicePeriod {
    MONTHLY = 'MONTHLY',
    SEMI_WEEKLY = 'SEMI_WEEKLY',
    WEEKLY = 'WEEKLY',
}

export enum CertificationStatus {
    ALL = 'ALL',
    NOT_CERTIFIED = 'NOT_CERTIFIED',
    FULLY_CERTIFIED = 'FULLY_CERTIFIED',
}

export enum InvoiceDeliveryMethod {
    ALL = 'ALL',
    SCHEDULED_ELECTRONIC = 'SCHEDULED_ELECTRONIC',
    ELECTRONIC_BILLING = 'ELECTRONIC_BILLING',
    PRINT = 'PRINT',
    EMAIL = 'EMAIL',
    EXTERNAL_INVOICE_SYSTEM = 'EXTERNAL_INVOICE_SYSTEM',
}
export enum OrderOnDate {
    NO_DATE_FILTER = 'NO_DATE_FILTER',
    ORDER_DELIVERED = 'ORDER_DELIVERED',
    ORDER_CREATED = 'ORDER_CREATED',
}

export enum InvoiceNoteStatus {
    INVOICED = 'INVOICED',
    NOT_INVOICED = 'NOT_INVOICED',
}

export type MonetaryTotal = {
    currency: string;
    total: number;
    vat: number;
    totalIncVat: number;
};

export type ClientInvoiceSettings = {
    invoiceType: InvoicingType;
    invoiceDelivery: InvoiceDelivery;
    invoiceEmail: string;
};

export type ReviewOrders = {
    _id: string;
    orderId: string;
    currency: string;
    orderStatus: OrderStatus;
    clinicName: string;
    ptjCustomerNumber?: string;
    clinicId: string;
    parentClinicId: string;
    deliveryDate: Date;
    orderCreatedDate: Date;
    tempInvoiceNumber: string;
    invoicingState: InvoicingState;
    isProductionFinished?: boolean;
    invoiceNumber: string;
    pdfUrl: string;
    invoiceType: string;
    clinicInvoiceType?: string;
    clinicInvoiceDeliveryMethod?: string;
    clinicInvoiceEmail?: string;
    invoiceDeliveryMethod: string;
    invoiceStatus: string;
    invoiceDate: Date;
    invoiceSentDate: Date;
    // priceTotal: MonetaryTotal;
    totalAmount: TotalAmount;
    clientInvoiceSettings: ClientInvoiceSettings;
};

export type OrderReviewFilterType = {
    orderIdOrInvoiceNumber?: string;
    clinicOrganizationId?: string;
    clinic?: Organization | null;
    orderStatus?: string[];
    invoicingState?: string;
    invoiceType?: string;
    deliveryMethod?: string;
    dateFilterType?: string;
    dateFromTo?: DateRange<Date | null>;
    production?: string;
};

export type OrdersForCollectiveInvoice = {
    _id: string;
    orderId: string;
    patientName: string;
    totalAmount: TotalAmount;
};
export type CollectiveInvoiceSummary = {
    clinic: string;
    clinicName: string;
    currency: string;
    ptjCustomerNumber?: string;
    dentist?: string;
    dentistName?: string;
    key: string;
    displayLabel?: string;
    deliveryMethod: string;
    invoiceType: string;
    emails: string[];
    paymentTerm: number;
    totalPrice: number;
    noOfOrders: number;
    lineItemMap?: Map<number, InvoiceLineItem>;
    lineItems: InvoiceLineItem[];
};

export type InvoiceLineItem = {
    order?: Order | string;
    currency?: string;
    note?: InvoiceNote | string;
    description: string;
    articleNo: string;
    quantity: number;
    itemPrice: number;
    itemVat: number;
    itemVatPercentage: number;
    /** DD-3700: INTRA_EU_SUPPLY for a line sold VAT free to an EU customer with a VAT number. */
    vatTreatment?: string;
    totalIncVat: number;
    isManual?: boolean;
    isIdentifyingRow?: boolean;
    totalExclVat?: number;
    id?: number;
};

export enum Type {
    DEBIT = 'DEBIT',
    CREDIT = 'CREDIT',
}

export type Invoice = {
    _id: string;
    mainClinic: Organization;
    dentist?: User;
    invoiceNumber: string;
    currency: string;
    invoiceText?: string;
    supplier: InvoiceParty;
    customer: InvoiceParty;
    deliveryAddress: InvoiceParty;
    pdfUrl: string;
    deliveryNotesPdfUrl: string;
    invoiceSpecificationPdfUrl: string;
    invoiceType: string;
    createdDate: Date;
    invoiceDate: Date;
    dueDate: Date;
    sentDate: Date;
    paidOn?: Date;
    paymentStatus?: InvoicePaymentStatus;
    status: string;
    processingErrorMessage?: string;
    gln?: string;
    vatNumber?: string;
    ocrNumber?: string;
    costCenter?: string;
    deliveryMethod: string;
    total: MonetaryTotal;
    lineItems: InvoiceLineItem[];
    paymentTerm: number;
    invoiceSettings?: InvoiceSettings;
    description: string;
    orderNumber: string;
    paymentDetails: PaymentDetails;
    lineItemWithOrderCount?: number;
    certifiedOrderCount?: number;
    type: Type;
    debitInvoice?: Invoice;
    creditInvoices?: Invoice[];
    isCredited?: boolean;
    orders: string[];
    order: string | Order;
    ourReference?: string;
};

export type InvoiceParty = {
    name: string;
    number: string;
    reference: string;
    contact: OrganizationContact;
    countryCode: string;
};

export type InvoiceFilter = {
    invoiceNumber?: string;
    clinicOrganizationId?: string | null;
    clinic?: Organization | null;
    labId?: string;
    lab?: ConnectedLab | null;
    invoiceStatus?: InvoiceStatus;
    invoiceType?: string;
    myInvoice?: boolean;
    deliveryMethod?: string;
    paymentStatus?: string;
    dateFilterType?: string;
    certificationStatus?: string;
    dateFromTo?: DateRange<Date | null>;
    yourReference?: string;
};

export enum InvoicePaymentStatus {
    ALL = 'ALL',
    PAID = 'PAID',
    UNPAID = 'UNPAID',
}

export enum InvoiceDate {
    NO_DATE_FILTER = 'NO_DATE_FILTER',
    INVOICE_SENT = 'INVOICE_SENT',
    INVOICE_DATE = 'INVOICE_DATE',
}

export type CollectiveInvoice = {
    clinic: string;
    clinicName: string;
    ptjCustomerNumber?: string;
    dentist: string;
    paymentTerm: number;
    currency: string;
    invoiceNumber?: string;
    invoiceDate: Date;
    lineItems: InvoiceLineItem[];
    orders: string[];
};

export type InvoiceItemContextEvent = {
    invoice: Invoice;
    mouseX: number;
    mouseY: number;
};

export type EmailPdf = {
    invoiceId: string;
    replyAddress: string;
    to: string[];
    cc: string[];
    bcc: string[];
    subject?: string;
    body: string;
};

export type InvoiceNoteFilter = {
    clinicOrganizationId?: string | null;
    clinic?: Organization | null;
    invoiceNoteStatus?: InvoiceNoteStatus;
    search?: string;
};

export type InvoiceNote = {
    _id?: string;
    type: Type;
    currency?: string;
    lab?: string;
    clinic: Organization;
    dentist?: User | undefined;
    invoice?: Invoice;
    description: string;
    quantity: number;
    itemPrice: number;
    itemVatPercentage: number;
    itemVat: number;
    totalExclVat: number;
    totalIncVat: number;
};

export enum OrderStatusMultiSelect {
    SENT_BY_CUSTOMER = 'SENT_BY_CUSTOMER',
    ACCEPTED_BY_LAB = 'ACCEPTED_BY_LAB',
    SENT_BY_LAB = 'SENT_BY_LAB',
    ORDER_APPROVED = 'ORDER_APPROVED',
    CANCELLED = 'CANCELLED',
    DECLINED = 'DECLINED',
}

export enum InvoiceListType {
    MY_INVOICES = 'MY_INVOICES',
    ALL_INVOICES = 'ALL_INVOICES',
}
